// @noflow
import {
  PuppyDiscountModalQuery,
  PuppyDiscountModalQueryVariables
} from '@/gql/graphql'
import { useQuery } from '@apollo/client'
import { addDays } from 'date-fns'
import Cookies from 'js-cookie'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { Product, captureException } from '@/utils/sentry'

import { Analytics } from '@/components/analytics/Analytics'
import useUpdateDiscountCode from '@/components/pages/SimplifiedPlansPage/hooks/UseUpdateDiscountCode/useUpdateDiscountCode'

import { PUPPY_DISCOUNT_MODAL_QUERY } from './queries/PuppyDiscountModal'

import CountdownModal from '../CountdownModal/CountdownModal'
import NotificationContent from '../NotificationContent/NotificationContent'

type Props = {
  isModalOpen: boolean
  setOpenModal: (isOpen: boolean) => void
}

const PUPPY_DISCOUNT_MODAL_COOKIE = 'has_closed_puppy_discount_modal'

const PuppyDiscountModal = ({
  isModalOpen,
  setOpenModal
}: Props): JSX.Element | null => {
  const namespace = 'simplified_plans_flow'
  const copyContext = 'puppy_discount_modal'

  const discountCodeCookiesKey = 'discount_code_id'
  const discountCodeId = Cookies.get(discountCodeCookiesKey)

  const [discountCode, setDiscountCode] = useState<
    PuppyDiscountModalQuery['discountCode'] | null
  >(null)

  const handleError = useCallback((error: Error, sentryMessage) => {
    captureException(sentryMessage, {
      extra: {
        error: error.message
      },
      tags: {
        product: Product.PlansFlow
      }
    })

    toast.error(
      <NotificationContent
        copy={{
          text: `${copyContext}.error`,
          namespace
        }}
      />,
      { toastId: 'puppy-discount-error-message' }
    )
  }, [])

  const setModalCookie = useCallback(() => {
    Cookies.set(PUPPY_DISCOUNT_MODAL_COOKIE, 'true', {
      path: '/plans'
    })
  }, [])

  const toggleModal = useCallback(
    (open: boolean) => {
      if (!open) {
        Analytics.track('Puppy Discount Modal closed', {
          channel: discountCode?.channel
        })
        // set cookie to prevent showing the modal again
        setModalCookie()
      }
      setOpenModal(open)
    },
    [discountCode?.channel, setModalCookie, setOpenModal]
  )

  const onSuccess = useCallback(() => {
    setModalCookie()

    toggleModal(false)

    toast.success(
      <NotificationContent
        copy={{
          text: `${copyContext}.success`,
          namespace
        }}
      />,
      { toastId: 'puppy-discount-alert-success-message' }
    )
  }, [setModalCookie, toggleModal])

  const onError = useCallback(
    (error) => {
      handleError(
        error,
        'Error received in Puppy Discount Modal update discount code mutation'
      )
    },
    [handleError]
  )

  const { redeemDiscount, redeemDiscountLoading, updateSessionDiscountCode } =
    useUpdateDiscountCode(onError, onSuccess)

  // Fetch the current discount code
  const { loading } = useQuery<
    PuppyDiscountModalQuery,
    PuppyDiscountModalQueryVariables
  >(PUPPY_DISCOUNT_MODAL_QUERY, {
    skip: !discountCodeId,
    variables: {
      discountCodeId: discountCodeId || ''
    },
    onCompleted: ({ discountCode }) => {
      setDiscountCode(discountCode)
      updateSessionDiscountCode(discountCode.code)
    },
    onError: (error) => {
      handleError(error, 'Error received in PUPPY_DISCOUNT_MODAL_QUERY')
    }
  })

  const showModal = Cookies.get(PUPPY_DISCOUNT_MODAL_COOKIE) !== 'true'

  const today = Date.now()
  const oneDayFromNow = addDays(today, 1)

  const onClick = useCallback(() => {
    // close modal, set discount code and analytics
    let newDiscountCode
    switch (discountCode?.channel?.toLowerCase()) {
      case 'paid_social':
        newDiscountCode = 'puppysocial5025'
        break
      case 'google':
        newDiscountCode = 'puppygoogle5025'
        break
      default:
        newDiscountCode = 'puppy5025'
        break
    }

    Analytics.track('Puppy Discount Modal Redeem Clicked', {
      discountCode: newDiscountCode,
      channel: discountCode?.channel
    })

    redeemDiscount({ variables: { discountCode: newDiscountCode } })
  }, [discountCode, redeemDiscount])

  return (
    <CountdownModal
      isModalOpen={isModalOpen && showModal}
      toggleModal={toggleModal}
      heroImageSlug="puppy-discount-modal-hero"
      heroImageAlt={`${copyContext}.hero_image_alt`}
      countdownEndDate={oneDayFromNow}
      headingText={{
        text: `${copyContext}.header`,
        namespace: namespace,
        variant: 'display24',
        align: 'center',
        margin: false
      }}
      descriptionText={{
        text: `${copyContext}.description`,
        namespace: namespace,
        variant: 'textRegular16',
        align: 'center'
      }}
      footerText={{
        text: `${copyContext}.footer`,
        namespace: namespace,
        variant: 'textRegular14',
        colour: 'brandBlue400',
        align: 'center',
        margin: false
      }}
      buttonText={{
        text: `${copyContext}.cta`,
        namespace: namespace
      }}
      buttonOnClick={onClick}
      loading={loading || redeemDiscountLoading}
    />
  )
}

export default PuppyDiscountModal
